import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Copy, AlertTriangle } from 'lucide-react';
import { toast } from "sonner";

export default function DomainSetupPage() {

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`Copied: ${text}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-black text-gray-900 mb-4">Custom Domain Setup Guide</h1>
          <p className="text-xl text-gray-600">Follow these steps to correctly point your domain to your base44 application.</p>
        </div>

        <Alert className="mb-8 bg-yellow-50 border-yellow-300">
          <AlertTriangle className="h-5 w-5 text-yellow-700" />
          <AlertTitle className="font-bold text-yellow-900">Why am I seeing a "404 Not Found" error?</AlertTitle>
          <AlertDescription className="text-yellow-800">
            This error usually means your custom domain's DNS records are not correctly configured to handle all pages of your application. Following the guide below will resolve this issue.
          </AlertDescription>
        </Alert>

        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle>Step 1: Log in to Your Domain Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">Log in to the dashboard where you purchased your domain (e.g., GoDaddy, Namecheap, Cloudflare, Google Domains, etc.). Navigate to the DNS management section for <strong>eztirechanger.com</strong>.</p>
          </CardContent>
        </Card>

        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle>Step 2: Add DNS Records</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">A Record (for the root domain eztirechanger.com)</h3>
              <p className="text-gray-600 mb-4">Create or update the 'A' record for your root domain.</p>
              <div className="bg-gray-100 p-4 rounded-lg space-y-2 border">
                <p><strong>Type:</strong> A</p>
                <p><strong>Name/Host:</strong> @</p>
                <div className="flex items-center gap-2">
                  <p><strong>Value/Points to:</strong> 76.76.21.21</p>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard('76.76.21.21')}>
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </div>
                <p><strong>TTL:</strong> 1 hour (or as low as possible)</p>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">CNAME Record (for www.eztirechanger.com)</h3>
              <p className="text-gray-600 mb-4">Create or update the 'CNAME' record for the 'www' subdomain.</p>
              <div className="bg-gray-100 p-4 rounded-lg space-y-2 border">
                <p><strong>Type:</strong> CNAME</p>
                <p><strong>Name/Host:</strong> www</p>
                <div className="flex items-center gap-2">
                  <p><strong>Value/Target:</strong> cname.base44.com</p>
                  <Button variant="ghost" size="sm" onClick={() => copyToClipboard('cname.base44.com')}>
                    <Copy className="w-4 h-4 mr-2" /> Copy
                  </Button>
                </div>
                <p><strong>TTL:</strong> 1 hour (or as low as possible)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8 shadow-lg">
          <CardHeader>
            <CardTitle>Step 3: Wait for DNS Propagation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">DNS changes can take anywhere from a few minutes to 48 hours to fully propagate across the internet. You can check the status using a DNS checker tool.</p>
            <a href="https://dnschecker.org/#A/eztirechanger.com" target="_blank" rel="noopener noreferrer">
              <Button>Check DNS Status</Button>
            </a>
          </CardContent>
        </Card>
        
        <Alert className="bg-red-50 border-red-300">
            <AlertTriangle className="h-5 w-5 text-red-700"/>
            <AlertTitle className="font-bold text-red-900">Immediate Workaround</AlertTitle>
            <AlertDescription className="text-red-800">
                While you wait for your domain to be fixed, you can use the original base44 URL to upload your file. It is fully functional:
                <br />
                <a href="https://lctxaustralia.base44.app/Upload3DModel" target="_blank" rel="noopener noreferrer" className="font-bold underline mt-2 inline-block">
                    https://lctxaustralia.base44.app/Upload3DModel
                </a>
            </AlertDescription>
        </Alert>

      </div>
    </div>
  );
}